/**
 * Keeping the next few slides decrypted, and nothing else.
 *
 * A slideshow is the one screen that knows exactly what comes next, which makes
 * it the one screen that can prepare. It is also the one screen where a stall
 * is unmissable: a grid that loads a tile late is untidy, a slideshow that
 * loads a slide late has stopped.
 *
 * So a small window either side of the current slide is held decrypted and
 * everything outside it is released. Holding the whole show would mean an
 * eight-hundred-photograph trip decrypted into memory at once, which no phone
 * will survive; holding only the current slide means every advance waits for a
 * fetch and a decrypt.
 */

import { useEffect, useRef, useState } from "react";
import { trpcClient } from "../../lib/trpc.js";
import { acquireDecryptedUrl, release } from "../../lib/mediaCache.js";
import { imageAccept } from "../../lib/imageSupport.js";
import { URL_BATCH } from "../../lib/signedUrls.js";

/** A signed URL and the key that opens it. */
interface Signed {
  url: string;
  wrappedFileKey: string | null;
}

/** The server refuses very large batches, and a trip can be long. */
/*
 * Bounded by URL length, not by taste.
 *
 * A query carries its ids in the query string, and a hundred of them is a
 * 3,800-character URL against a 2,048 limit — so this chunking looked careful
 * and still produced requests that could not be sent. `URL_BATCH` is the
 * measured figure and lives in one place now.
 */
const CHUNK = URL_BATCH;

/** Slides kept ready ahead of the current one, and behind it for going back. */
const AHEAD = 3;
const BEHIND = 1;

export interface SlideshowMedia {
  /** Decrypted object URLs, by file id. Absent means not ready yet. */
  urls: ReadonlyMap<string, string>;
  /** True once the first slide can be shown. */
  started: boolean;
  failed: boolean;
}

export function useSlideshowMedia(
  groupId: string,
  ids: readonly string[],
  index: number,
): SlideshowMedia {
  const [urls, setUrls] = useState<ReadonlyMap<string, string>>(new Map());
  const [failed, setFailed] = useState(false);

  /** Signed URLs for the whole show, fetched once. */
  const signed = useRef(new Map<string, Signed>());
  const [signedReady, setSignedReady] = useState(false);

  /** What is currently decrypted, so it can be given back. */
  const held = useRef(new Map<string, string>());

  const key = ids.join(",");

  useEffect(() => {
    let live = true;
    signed.current = new Map();
    setSignedReady(false);
    setFailed(false);

    void (async () => {
      try {
        const accept = await imageAccept();
        for (let start = 0; start < ids.length; start += CHUNK) {
          const batch = ids.slice(start, start + CHUNK);
          const result = await trpcClient.media.urls.query({
            groupId,
            fileIds: batch,
            /*
             * The large preview, not the original. A television is at most four
             * thousand pixels across and a viewer is six feet away; fetching
             * forty-megapixel originals for that would cost real money in
             * egress and buy nothing anybody can see.
             */
            variant: "preview_hq",
            accept,
          });
          if (!live) return;
          for (const entry of result.urls) {
            if (entry.url) {
              signed.current.set(entry.fileId, {
                url: entry.url,
                wrappedFileKey: entry.wrappedFileKey ?? null,
              });
            }
          }
        }
        if (live) setSignedReady(true);
      } catch {
        if (live) setFailed(true);
      }
    })();

    return () => {
      live = false;
    };
    /*
     * Keyed on the joined ids, not on the array.
     *
     * `ids` is rebuilt by its caller on every render, so depending on it would
     * re-sign every URL in the show each time anything re-rendered — hundreds
     * of requests for a list that had not changed. The joined string changes
     * exactly when the contents do, which is the condition that actually
     * matters, and the effect closes over a fresh `ids` whenever it does.
     */
  }, [groupId, key]);

  // The moving window.
  useEffect(() => {
    if (!signedReady) return;
    let live = true;

    const wanted = new Set(
      ids.slice(Math.max(0, index - BEHIND), index + AHEAD + 1),
    );

    // Give back anything that has left the window, before fetching more — on a
    // phone the memory freed here is what makes room for what follows.
    for (const [id, objectUrl] of held.current) {
      if (!wanted.has(id)) {
        release(objectUrl);
        held.current.delete(id);
      }
    }
    setUrls(new Map(held.current));

    void (async () => {
      for (const id of wanted) {
        if (!live) return;
        if (held.current.has(id)) continue;

        const source = signed.current.get(id);
        if (!source) continue;

        try {
          const objectUrl = await acquireDecryptedUrl({
            url: source.url,
            wrappedFileKey: source.wrappedFileKey,
            groupId,
          });
          if (!live) {
            release(objectUrl);
            return;
          }
          held.current.set(id, objectUrl);
          setUrls(new Map(held.current));
        } catch {
          // One unreadable slide is skipped, never the whole show.
        }
      }
    })();

    return () => {
      live = false;
    };
  }, [signedReady, index, key, groupId]);

  // Everything goes back when the show ends.
  useEffect(() => {
    const outstanding = held.current;
    return () => {
      for (const objectUrl of outstanding.values()) release(objectUrl);
      outstanding.clear();
    };
  }, []);

  const current = ids[index];
  return { urls, started: current !== undefined && urls.has(current), failed };
}
